import type { ReactNode } from 'react'
import { Explicacao, type ChaveComAjuda } from './Explicacao'
import { cn } from '../lib/cn'
import { TEXTOS } from '../lib/glossario'

export interface Dado {
  rotulo: string
  valor: ReactNode
  /**
   * Termo do glossário a explicar ao lado do rótulo.
   *
   * Existe porque as siglas do contrato — ICA, IAP, TIS — apareciam cruas em várias telas e a
   * frase que as traduz estava escrita no glossário sem chegar a ninguém.
   */
  ajuda?: ChaveComAjuda
}

/**
 * Pares rótulo/valor em `<dl>`.
 *
 * `<dl>` e não duas colunas de `<div>` porque o leitor de tela precisa saber que
 * "Chamado" é o rótulo de "2026051281" — sem a marcação, os dois viram texto solto e a
 * pessoa ouve uma lista de palavras sem relação.
 *
 * @param props os pares a exibir
 * @returns a lista de definições
 */
export function ListaDeDados({ dados }: { dados: Dado[] }) {
  return (
    <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
      {dados.map((dado) => (
        <div key={dado.rotulo} className="min-w-0">
          <dt className="flex items-center gap-1 text-xs font-medium text-slate-500">
            {dado.rotulo}
            {dado.ajuda && <Explicacao termo={dado.ajuda} className="h-3.5 w-3.5" />}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">{dado.valor || TEXTOS.naoInformado}</dd>
        </div>
      ))}
    </dl>
  )
}

interface PropsCartao {
  titulo: string
  children: ReactNode
  acao?: ReactNode
  /** Uma linha de contexto sob o título, para explicar a seção a quem não é de TI. */
  descricao?: string
  className?: string
}

/**
 * Bloco de conteúdo com título, usado nas seções do detalhe.
 *
 * O título é `<h2>` de verdade, e não um `<div>` em negrito: é assim que quem navega por
 * leitor de tela pula de seção em seção em vez de ouvir a página inteira em ordem. O
 * cabeçalho fica separado do conteúdo por uma linha em vez de por espaço, porque as telas
 * de detalhe empilham quatro ou cinco cartões e o olho precisa de onde um termina.
 *
 * @param props título, descrição opcional, ação do canto e o conteúdo
 * @returns a seção
 */
export function Cartao({ titulo, children, acao, descricao, className }: PropsCartao) {
  return (
    <section className={cn('cartao overflow-hidden', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>
          {descricao && <p className="mt-0.5 text-sm text-slate-500">{descricao}</p>}
        </div>
        {acao}
      </div>
      <div className="px-5 py-5">{children}</div>
    </section>
  )
}
